import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Clock3, Copy, Loader2, MessageSquare, RefreshCw, Save, Share2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import {
  createServiceTemplate,
  createFinancingQuote,
  FinancingInsights,
  FinancingQuoteResponse,
  getFinancingInsights,
  listServiceTemplates,
  listFinancingQuotes,
  SellerQuoteStatus,
  ServiceTemplate,
  trackFinancingQuoteShareEvent,
} from '@/lib/lendliftedService';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useWorkspacePayoutStatus } from '@/hooks/useWorkspacePayoutStatus';
import { useAppStore } from '@/stores/appStore';
import { logFirebaseEvent } from '@/lib/tracking';

const POLL_INTERVAL_MS = 15000;
const LOADING_TIP_INTERVAL_MS = 4200;

const STUDIO_LOADING_TIPS = [
  'Tip: Start with only the total amount. Everything else is optional.',
  'Tip: Show the QR in-person so the customer can check out immediately.',
  'Tip: Say “as low as monthly payments” before sharing the link.',
  'Tip: Use SMS share when the customer is standing with you.',
];

type FollowUpTone = 'gentle' | 'time_sensitive' | 'help_first';
type MultiTierInput = { name: string; amount: string; description: string };

const FOLLOW_UP_TONES: Array<{ id: FollowUpTone; label: string; helper: string }> = [
  { id: 'gentle', label: 'Gentle', helper: 'Friendly reminder with no pressure' },
  { id: 'time_sensitive', label: 'Time-sensitive', helper: 'Highlights timing and urgency' },
  { id: 'help_first', label: 'Help-first', helper: 'Offers help before asking for action' },
];

const formatMinutesSince = (iso: string): string => {
  const deltaMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(1, Math.floor(deltaMs / (1000 * 60)));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return `${hours}h ${remainder}m ago`;
};

const formatCurrency = (amount: number, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
};

const formatCountdown = (expiresAt: string | null): string => {
  if (!expiresAt) return 'No expiry';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${mins}m left`;
};

const statusStyleMap: Record<SellerQuoteStatus['status'], string> = {
  open: 'bg-emerald-100 text-emerald-800',
  checkout_started: 'bg-amber-100 text-amber-900',
  paid: 'bg-blue-100 text-blue-900',
  deposit_paid: 'bg-cyan-100 text-cyan-900',
  expired: 'bg-slate-200 text-slate-700',
};

const statusLabelMap: Record<SellerQuoteStatus['status'], string> = {
  open: 'Open',
  checkout_started: 'Checkout started',
  paid: 'Paid',
  deposit_paid: 'Deposit paid',
  expired: 'Expired',
};

const CELEBRATION_DURATION_MS = 5000;

const playChaChing = () => {
  if (typeof window === 'undefined') return;
  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtx) return;
  const ctx = new AudioCtx();
  const now = ctx.currentTime;
  const sequence = [
    { freq: 880, start: 0, duration: 0.09 },
    { freq: 1320, start: 0.1, duration: 0.09 },
    { freq: 1760, start: 0.22, duration: 0.12 },
  ];

  sequence.forEach((note) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(note.freq, now + note.start);
    gain.gain.setValueAtTime(0.0001, now + note.start);
    gain.gain.exponentialRampToValueAtTime(0.12, now + note.start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + note.start + note.duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + note.start);
    osc.stop(now + note.start + note.duration + 0.02);
  });

  window.setTimeout(() => {
    void ctx.close().catch(() => undefined);
  }, 700);
};

function PaidCelebrationOverlay({ visible }: { visible: boolean }) {
  if (!visible) return null;

  const pieces = Array.from({ length: 24 }, (_, index) => {
    const left = `${(index / 24) * 100 + ((index % 3) - 1) * 1.5}%`;
    const delay = `${(index % 8) * 0.06}s`;
    const duration = `${2.6 + (index % 5) * 0.22}s`;
    const rotation = `${(index * 37) % 360}deg`;
    const colors = ['#0f172a', '#22c55e', '#f59e0b', '#3b82f6'];
    return { left, delay, duration, rotation, color: colors[index % colors.length] };
  });

  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-x-0 top-6 flex justify-center">
        <div className="rounded-full border border-emerald-200 bg-white/95 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm">
          Payment received
        </div>
      </div>
      {pieces.map((piece, index) => (
        <span
          key={index}
          className="absolute top-[-10px] h-3 w-2.5 rounded-sm"
          style={{
            left: piece.left,
            backgroundColor: piece.color,
            transform: `rotate(${piece.rotation})`,
            animation: `ll-confetti-fall ${piece.duration} ease-in forwards`,
            animationDelay: piece.delay,
          }}
        />
      ))}
      <style>{`
        @keyframes ll-confetti-fall {
          0% { transform: translateY(-10px) rotate(0deg); opacity: 0; }
          8% { opacity: 1; }
          100% { transform: translateY(115vh) rotate(540deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function SellerStudioPage() {
  const { userSettings } = useAuth();
  const { requestPermissionAndGetToken } = usePushNotifications();
  const soundEnabled = useAppStore((state) => state.soundEnabled);
  const [totalAmount, setTotalAmount] = useState('');
  const [description, setDescription] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositOnlyCheckoutMode, setDepositOnlyCheckoutMode] = useState(false);
  const [multiTierEnabled, setMultiTierEnabled] = useState(false);
  const [multiTierInputs, setMultiTierInputs] = useState<MultiTierInput[]>([
    { name: 'Basic', amount: '', description: '' },
    { name: 'Standard', amount: '', description: '' },
    { name: 'Premium', amount: '', description: '' },
  ]);
  const [tippingEnabled, setTippingEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [quote, setQuote] = useState<FinancingQuoteResponse | null>(null);
  const [recentQuotes, setRecentQuotes] = useState<SellerQuoteStatus[]>([]);
  const [insights, setInsights] = useState<FinancingInsights | null>(null);
  const [serviceTemplates, setServiceTemplates] = useState<ServiceTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('__none__');
  const [hasTrackedQrForQuote, setHasTrackedQrForQuote] = useState(false);
  const [loadingTipIndex, setLoadingTipIndex] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState<'granted' | 'denied' | 'default' | 'unsupported'>('unsupported');
  const [followUpTone, setFollowUpTone] = useState<FollowUpTone>('gentle');
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const hasInitializedActivityRef = useRef(false);
  const previousViewedAtRef = useRef<Record<string, string | null>>({});
  const hasInitializedPaidRef = useRef(false);
  const previousStatusRef = useRef<Record<string, SellerQuoteStatus['status']>>({});
  const celebratedPaidQuoteIdsRef = useRef<Set<string>>(new Set());
  const celebrationTimeoutRef = useRef<number | null>(null);
  const hasAudioInteractionRef = useRef(false);
  const [showPaidCelebration, setShowPaidCelebration] = useState(false);

  const workspaceId = userSettings?.activeWorkspaceId;
  const workspaceSlug = userSettings?.activeWorkspace?.slug || null;
  const workspaceName = userSettings?.activeWorkspace?.name || 'Your Studio';
  const payoutStatus = useWorkspacePayoutStatus(workspaceId);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const openLinkUrl = workspaceSlug ? `${window.location.origin}/open/${encodeURIComponent(workspaceSlug)}` : null;

  useEffect(() => {
    const markInteraction = () => {
      hasAudioInteractionRef.current = true;
    };
    window.addEventListener('pointerdown', markInteraction, { once: true });
    window.addEventListener('keydown', markInteraction, { once: true });
    return () => {
      window.removeEventListener('pointerdown', markInteraction);
      window.removeEventListener('keydown', markInteraction);
    };
  }, []);

  const writeClipboardText = async (text: string, fallbackMessage = 'Could not copy automatically. Please copy manually.') => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Clipboard copy failed', error);
      toast.info(fallbackMessage);
      return false;
    }
  };

  const loadRecentQuotes = async (silent = false) => {
    if (!workspaceId) return;
    if (!silent) setIsLoadingQuotes(true);
    try {
      const quotes = await listFinancingQuotes(workspaceId, 12);
      setRecentQuotes(quotes);
    } catch (error) {
      console.error('Failed to load quote status list', error);
      if (!silent) toast.error('Could not load quote statuses.');
    } finally {
      if (!silent) setIsLoadingQuotes(false);
    }
  };

  const loadInsights = async (silent = false) => {
    if (!workspaceId) return;
    if (!silent) setIsLoadingInsights(true);
    try {
      const data = await getFinancingInsights(workspaceId, 30);
      setInsights(data);
    } catch (error) {
      console.error('Failed to load financing insights', error);
    } finally {
      if (!silent) setIsLoadingInsights(false);
    }
  };

  const loadServiceTemplateOptions = async (silent = false) => {
    if (!workspaceId) return;
    if (!silent) setIsLoadingTemplates(true);
    try {
      const templates = await listServiceTemplates(workspaceId, 20);
      setServiceTemplates(templates);
      setSelectedTemplateId((current) => {
        if (current === '__none__') return current;
        return templates.some((template) => template.templateId === current) ? current : '__none__';
      });
    } catch (error) {
      console.error('Failed to load service templates', error);
      if (!silent) toast.error('Could not load service templates.');
    } finally {
      if (!silent) setIsLoadingTemplates(false);
    }
  };

  useEffect(() => {
    loadRecentQuotes();
    loadInsights();
    loadServiceTemplateOptions();
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setNotificationPermission('unsupported');
      return;
    }
    setNotificationPermission(Notification.permission);
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    const timer = window.setInterval(() => {
      loadRecentQuotes(true);
      loadInsights(true);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [workspaceId]);

  useEffect(() => {
    if (!isLoadingQuotes || recentQuotes.length > 0) return;
    const timer = window.setInterval(() => {
      setLoadingTipIndex((prev) => (prev + 1) % STUDIO_LOADING_TIPS.length);
    }, LOADING_TIP_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isLoadingQuotes, recentQuotes.length]);

  useEffect(() => {
    if (recentQuotes.length === 0) return;

    const currentMap = Object.fromEntries(recentQuotes.map((item) => [item.quoteId, item.viewedAt || null]));
    if (!hasInitializedActivityRef.current) {
      previousViewedAtRef.current = currentMap;
      hasInitializedActivityRef.current = true;
      return;
    }

    const justViewed = recentQuotes.find((item) => {
      const prev = previousViewedAtRef.current[item.quoteId];
      return item.status === 'open' && !prev && !!item.viewedAt;
    });

    if (justViewed) {
      toast.info(`A customer viewed quote #${justViewed.quoteId.slice(0, 8)}. Perfect time for a follow-up.`);
      if (notificationPermission === 'granted' && typeof window !== 'undefined' && 'Notification' in window) {
        try {
          new Notification('Customer viewed your financing link', {
            body: 'Send a quick follow-up while interest is high.',
          });
        } catch (error) {
          console.error('Failed to show browser notification', error);
        }
      }
    }

    previousViewedAtRef.current = currentMap;
  }, [recentQuotes, notificationPermission]);

  useEffect(() => {
    if (recentQuotes.length === 0) return;

    const currentMap = Object.fromEntries(recentQuotes.map((item) => [item.quoteId, item.status]));
    if (!hasInitializedPaidRef.current) {
      previousStatusRef.current = currentMap;
      recentQuotes.forEach((item) => {
        if (item.status === 'paid') celebratedPaidQuoteIdsRef.current.add(item.quoteId);
      });
      hasInitializedPaidRef.current = true;
      return;
    }

    const newlyPaid = recentQuotes.filter((item) => {
      const prev = previousStatusRef.current[item.quoteId];
      return item.status === 'paid' && prev && prev !== 'paid' && !celebratedPaidQuoteIdsRef.current.has(item.quoteId);
    });

    if (newlyPaid.length > 0) {
      newlyPaid.forEach((item) => celebratedPaidQuoteIdsRef.current.add(item.quoteId));
      setShowPaidCelebration(true);
      toast.success(
        newlyPaid.length === 1
          ? `Cha-ching. Quote #${newlyPaid[0].quoteId.slice(0, 8)} was paid.`
          : `Cha-ching. ${newlyPaid.length} quotes were paid.`
      );
      if (soundEnabled && hasAudioInteractionRef.current) {
        try {
          playChaChing();
        } catch (error) {
          console.error('Failed to play celebration sound', error);
        }
      }
      if (celebrationTimeoutRef.current) {
        window.clearTimeout(celebrationTimeoutRef.current);
      }
      celebrationTimeoutRef.current = window.setTimeout(() => {
        setShowPaidCelebration(false);
        celebrationTimeoutRef.current = null;
      }, CELEBRATION_DURATION_MS);
    }

    previousStatusRef.current = currentMap;
  }, [recentQuotes, soundEnabled]);

  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        window.clearTimeout(celebrationTimeoutRef.current);
      }
    };
  }, []);

  const qrCodeDataUrl = useMemo(() => {
    if (!quote?.shareUrl) return null;
    return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=2&data=${encodeURIComponent(quote.shareUrl)}`;
  }, [quote?.shareUrl]);

  useEffect(() => {
    if (!workspaceId || !quote?.quoteId || !qrCodeDataUrl || hasTrackedQrForQuote) return;
    trackFinancingQuoteShareEvent(workspaceId, quote.quoteId, 'qr')
      .then(() => setHasTrackedQrForQuote(true))
      .catch((error) => console.error('Failed to track QR share event', error));
  }, [workspaceId, quote?.quoteId, qrCodeDataUrl, hasTrackedQrForQuote]);

  const copyShareLink = async () => {
    if (!workspaceId || !quote?.shareUrl) return;
    const copied = await writeClipboardText(quote.shareUrl);
    try {
      await trackFinancingQuoteShareEvent(workspaceId, quote.quoteId, 'copy');
      loadRecentQuotes(true);
      loadInsights(true);
    } catch (error) {
      console.error('Failed to track copy share event', error);
    }
    if (copied) toast.success('Financing link copied.');
  };

  const applyServiceTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId === '__none__' || !templateId) {
      return;
    }
    const template = serviceTemplates.find((item) => item.templateId === templateId);
    if (!template) return;
    setDescription(template.description || template.name);
    setTotalAmount(String(template.defaultAmount));
    toast.success(`Loaded template: ${template.name}`);
  };

  const saveCurrentAsTemplate = async () => {
    if (!workspaceId) {
      toast.error('No active workspace found.');
      return;
    }

    const amount = Number(totalAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid total amount before saving a template.');
      return;
    }

    const suggestedName = description.trim() || customerName.trim() || 'New service';
    const name = window.prompt('Template name', suggestedName)?.trim();
    if (!name) return;

    setIsSavingTemplate(true);
    try {
      await createServiceTemplate({
        workspaceId,
        name,
        description: description.trim() || '',
        defaultAmount: amount,
        currency: 'usd',
      });
      toast.success('Service template saved.');
      await loadServiceTemplateOptions(true);
    } catch (error) {
      console.error('Failed to save service template', error);
      toast.error('Could not save service template.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const buildPrimaryShareMessage = (item: FinancingQuoteResponse) => {
    return `Your financing checkout link is ready for ${formatCurrency(item.financedAmount, 'USD')}. As low as ${formatCurrency(item.monthlyEstimate, 'USD')}/mo if eligible: ${item.shareUrl}`;
  };

  const shareNativeLink = async () => {
    if (!workspaceId || !quote?.shareUrl) return;

    if (!canNativeShare) {
      await copyShareLink();
      return;
    }

    try {
      await navigator.share({
        title: 'Your financing checkout link',
        text: buildPrimaryShareMessage(quote),
        url: quote.shareUrl,
      });
      await trackFinancingQuoteShareEvent(workspaceId, quote.quoteId, 'native');
      loadRecentQuotes(true);
      loadInsights(true);
      toast.success('Share sheet opened.');
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        return;
      }
      console.error('Native share failed', error);
      toast.error('Could not open share sheet. Link copied instead.');
      await copyShareLink();
    }
  };

  const followUpCandidate = useMemo(() => {
    const candidates = recentQuotes.filter((item) => item.status === 'open' && !!item.viewedAt && !!item.shareUrl);
    if (candidates.length === 0) return null;

    const score = (item: SellerQuoteStatus) => {
      const viewedAtScore = item.viewedAt ? Math.max(0, 240 - Math.floor((Date.now() - new Date(item.viewedAt).getTime()) / (1000 * 60))) : 0;
      const amountScore = Math.min(200, Math.round(item.financedAmount / 25));
      const engagementScore = Math.min(80, item.viewCount * 10);
      return viewedAtScore + amountScore + engagementScore;
    };

    return candidates.sort((a, b) => score(b) - score(a))[0];
  }, [recentQuotes]);

  const staleViewCandidate = useMemo(() => {
    return recentQuotes.find((item) => {
      if (item.status !== 'open' || !item.viewedAt) return false;
      const viewedMsAgo = Date.now() - new Date(item.viewedAt).getTime();
      return viewedMsAgo > 2 * 60 * 60 * 1000;
    }) || null;
  }, [recentQuotes]);

  const enableSmartAlerts = async () => {
    const token = await requestPermissionAndGetToken();
    if (token) {
      setNotificationPermission('granted');
      return;
    }
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  };

  const buildFollowUpMessage = (item: SellerQuoteStatus, tone: FollowUpTone) => {
    const amount = formatCurrency(item.financedAmount, item.currency);
    if (tone === 'time_sensitive') {
      return `Quick update: your financing checkout link for ${amount} is still active right now: ${item.shareUrl}. If you want monthly options, this is the fastest way to view them.`;
    }
    if (tone === 'help_first') {
      return `Happy to help if you have questions. Here’s your financing checkout link for ${amount}: ${item.shareUrl}. I can walk you through payment options if useful.`;
    }
    return `Hi! Here is your financing checkout link for ${amount}: ${item.shareUrl}. You can choose pay-over-time options at checkout if eligible.`;
  };

  const getQuotePurpose = (item: Pick<SellerQuoteStatus, 'description' | 'customerName'>) => {
    const descriptionValue = item.description?.trim();
    const customerValue = item.customerName?.trim();
    if (descriptionValue && customerValue) return `${descriptionValue} • ${customerValue}`;
    if (descriptionValue) return descriptionValue;
    if (customerValue) return `Customer: ${customerValue}`;
    return 'No description provided';
  };

  const copyFollowUpMessage = async (item: SellerQuoteStatus, tone: FollowUpTone) => {
    if (!workspaceId || !item.shareUrl) return;
    const copied = await writeClipboardText(buildFollowUpMessage(item, tone), 'Could not copy message automatically.');
    await trackFinancingQuoteShareEvent(workspaceId, item.quoteId, 'copy', tone);
    if (copied) toast.success('Follow-up message copied.');
    loadRecentQuotes(true);
    loadInsights(true);
  };

  const sendFollowUpSms = async (item: SellerQuoteStatus, tone: FollowUpTone) => {
    if (!workspaceId || !item.shareUrl) return;
    await trackFinancingQuoteShareEvent(workspaceId, item.quoteId, 'sms', tone);
    loadRecentQuotes(true);
    loadInsights(true);
    window.location.href = `sms:?&body=${encodeURIComponent(buildFollowUpMessage(item, tone))}`;
  };

  const createRefreshedQuote = async (item: SellerQuoteStatus) => {
    if (!workspaceId) return;
    setIsSubmitting(true);
    try {
      const refreshed = await createFinancingQuote({
        workspaceId,
        totalAmount: item.totalAmount,
        depositAmount: item.depositAmount,
        description: item.description || undefined,
        customerName: item.customerName || undefined,
        tippingEnabled,
      });
      setQuote(refreshed);
      setHasTrackedQrForQuote(false);
      const copied = await writeClipboardText(refreshed.shareUrl);
      await trackFinancingQuoteShareEvent(workspaceId, refreshed.quoteId, 'copy');
      if (copied) {
        toast.success('Fresh quote created and copied.');
      } else {
        toast.success('Fresh quote created.');
      }
      loadRecentQuotes(true);
      loadInsights(true);
    } catch (error) {
      console.error('Failed to create refreshed quote', error);
      toast.error('Could not refresh this quote.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const createRemainingBalanceQuote = async (item: SellerQuoteStatus) => {
    if (!workspaceId) return;
    const remainingBalance = Number(Math.max(0, item.totalAmount - item.depositAmount).toFixed(2));
    if (!(remainingBalance > 0)) {
      toast.error('No remaining balance to quote.');
      return;
    }

    setIsSubmitting(true);
    try {
      const createdQuote = await createFinancingQuote({
        workspaceId,
        totalAmount: remainingBalance,
        depositAmount: 0,
        checkoutMode: 'full',
        description: item.description ? `Remaining balance - ${item.description}` : 'Remaining balance',
        customerName: item.customerName || undefined,
        tippingEnabled,
      });
      logFirebaseEvent('quote_created_remaining_balance_followup', {
        source_quote_id: item.quoteId,
        quote_id: createdQuote.quoteId,
        remaining_amount: remainingBalance,
      });
      setQuote(createdQuote);
      setHasTrackedQrForQuote(false);
      const copied = await writeClipboardText(createdQuote.shareUrl);
      await trackFinancingQuoteShareEvent(workspaceId, createdQuote.quoteId, 'copy');
      if (copied) {
        toast.success('Remaining-balance quote created and copied.');
      } else {
        toast.success('Remaining-balance quote created.');
      }
      loadRecentQuotes(true);
      loadInsights(true);
    } catch (error) {
      console.error('Failed to create remaining-balance quote', error);
      toast.error('Could not create remaining-balance quote.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendSmsLink = () => {
    if (!workspaceId || !quote?.shareUrl) return;
    const amount = formatCurrency(quote.financedAmount, 'USD');
    const text = `Your financing checkout link: ${quote.shareUrl} (amount ${amount}).`;
    trackFinancingQuoteShareEvent(workspaceId, quote.quoteId, 'sms')
      .then(() => loadRecentQuotes(true))
      .catch((error) => console.error('Failed to track SMS share event', error));
    window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
  };

  const printDeskSign = () => {
    if (!openLinkUrl) {
      toast.error('Set up your Open Payment Link in Workspace Settings first.');
      return;
    }

    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=420x420&margin=2&data=${encodeURIComponent(openLinkUrl)}`;
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1000');
    if (!printWindow) {
      toast.error('Popup blocked. Please allow popups to print your desk sign.');
      return;
    }

    const escapedStudioName = workspaceName.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const escapedUrl = openLinkUrl.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    printWindow.document.write(`<!doctype html>
<html>
  <head>
    <title>Desk Sign - ${escapedStudioName}</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      @page { size: letter portrait; margin: 0.5in; }
      body { margin: 0; font-family: ui-sans-serif, system-ui, sans-serif; color: #0f172a; background: #ffffff; }
      .sheet { width: 100%; min-height: 10in; border: 2px solid #e2e8f0; border-radius: 24px; padding: 28px; box-sizing: border-box; background: linear-gradient(180deg,#f8fafc,#fff); display:flex; flex-direction:column; align-items:center; }
      .pill { font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: #64748b; font-weight: 700; }
      h1 { margin: 10px 0 0; font-size: 34px; line-height: 1.08; text-align:center; }
      .sub { margin: 10px 0 0; font-size: 16px; text-align:center; color:#334155; max-width: 520px; }
      .brand { margin-top: 6px; font-size: 14px; color:#475569; font-weight:600; }
      .qrWrap { margin-top: 20px; background:#fff; border:1px solid #e2e8f0; border-radius: 18px; padding: 16px; box-shadow: 0 8px 30px rgba(15,23,42,.06); }
      .qrWrap img { display:block; width: 380px; height:380px; }
      .steps { margin-top: 18px; width: 100%; max-width: 560px; display:grid; gap:10px; }
      .step { border:1px solid #e2e8f0; border-radius:14px; padding:12px 14px; background:#fff; font-size:14px; }
      .url { margin-top: 16px; font-size: 11px; color:#64748b; text-align:center; word-break: break-all; max-width: 540px; }
      .footer { margin-top:auto; padding-top:18px; font-size: 12px; color:#64748b; text-align:center; }
      @media print { .sheet { box-shadow:none; } }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="pill">Flexible Payments Available</div>
      <h1>Scan to See Monthly Payment Options</h1>
      <div class="brand">${escapedStudioName}</div>
      <p class="sub">Use your phone camera to scan. Enter your amount, choose your payment plan at secure checkout, and apply if eligible.</p>
      <div class="qrWrap"><img src="${qrCodeUrl}" alt="QR code for open payment link" /></div>
      <div class="steps">
        <div class="step"><strong>1.</strong> Scan the QR code</div>
        <div class="step"><strong>2.</strong> Enter the purchase amount and description</div>
        <div class="step"><strong>3.</strong> View payment options at checkout</div>
      </div>
      <div class="url">${escapedUrl}</div>
      <div class="footer">Tip: choose “Save as PDF” in the print dialog to download a PDF file.</div>
    </div>
    <script>
      const img = document.querySelector('img');
      const triggerPrint = () => setTimeout(() => window.print(), 250);
      if (img && !img.complete) img.addEventListener('load', triggerPrint, { once: true }); else triggerPrint();
    </script>
  </body>
</html>`);
    printWindow.document.close();
    toast.success('Desk sign opened. Use Print -> Save as PDF to download.');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!workspaceId) {
      toast.error('No active workspace found.');
      return;
    }

    const total = Number(totalAmount);
    const deposit = Number(depositAmount || '0');
    const validTiers = multiTierInputs
      .map((tier) => ({
        name: tier.name.trim(),
        amount: Number(tier.amount),
        description: tier.description.trim(),
      }))
      .filter((tier) => tier.name && Number.isFinite(tier.amount) && tier.amount > 0)
      .slice(0, 3);

    if (multiTierEnabled) {
      if (validTiers.length < 2) {
        toast.error('Add at least 2 valid tiers with names and amounts.');
        return;
      }
      if (depositOnlyCheckoutMode) {
        toast.error('Deposit-only checkout is not supported with multi-tier quotes yet.');
        return;
      }
      if (deposit > 0 && deposit >= Math.min(...validTiers.map((tier) => tier.amount))) {
        toast.error('Deposit must be less than your lowest tier amount.');
        return;
      }
    } else if (!Number.isFinite(total) || total <= 0 || deposit < 0 || deposit > total) {
      toast.error('Enter a valid total amount and optional deposit.');
      return;
    }

    setIsSubmitting(true);
    try {
      const createdQuote = await createFinancingQuote({
        workspaceId,
        totalAmount: multiTierEnabled ? Math.max(...validTiers.map((tier) => tier.amount)) : total,
        depositAmount: deposit,
        checkoutMode: depositOnlyCheckoutMode ? 'deposit_only' : 'full',
        tiers: multiTierEnabled ? validTiers : undefined,
        description: description.trim() || undefined,
        customerName: customerName.trim() || undefined,
        tippingEnabled,
      });
      if (multiTierEnabled && validTiers.length >= 2) {
        logFirebaseEvent('quote_created_multitier', {
          quote_id: createdQuote.quoteId,
          tier_count: validTiers.length,
          max_amount: Math.max(...validTiers.map((tier) => tier.amount)),
        });
      }
      setQuote(createdQuote);
      setHasTrackedQrForQuote(false);
      const copied = await writeClipboardText(createdQuote.shareUrl);
      await trackFinancingQuoteShareEvent(workspaceId, createdQuote.quoteId, 'copy');
      if (copied) {
        toast.success('Financing link created and copied.');
      } else {
        toast.success('Financing link created.');
      }
      loadRecentQuotes(true);
      loadInsights(true);
    } catch (error) {
      console.error('Failed to create financing quote', error);
      toast.error('Could not create financing link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <PaidCelebrationOverlay visible={showPaidCelebration} />
      <div className="rounded-3xl border border-slate-200/80 bg-white p-8 shadow-[0_24px_60px_-42px_rgba(15,23,42,0.35)]">
        <p className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-slate-600">
          Seller Financing Studio
        </p>
        <h1 className="mt-5 text-3xl font-bold tracking-[-0.04em] text-slate-950">Seller Studio</h1>
        <p className="mt-3 max-w-3xl text-[0.98rem] leading-7 tracking-[-0.01em] text-slate-600">Enter the amount, generate a buyer financing link, and close the sale with QR or SMS.</p>

        {!payoutStatus.isLoading && payoutStatus.pendingPayoutBalanceCents > 0 && (
          <div className="mt-6 rounded-2xl border border-cyan-200 bg-cyan-50/80 p-4">
            <p className="text-sm font-semibold text-slate-900">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                payoutStatus.pendingPayoutBalanceCents / 100
              )}{' '}
              is ready for cashout setup
            </p>
            <p className="mt-1 text-sm text-slate-700">
              You already have paid sales on platform hold. Complete payout setup to cash out {payoutStatus.pendingPayoutCount}{' '}
              held {payoutStatus.pendingPayoutCount === 1 ? 'payment' : 'payments'}.
            </p>
            <div className="mt-3">
              <div className="flex flex-wrap gap-2">
                <Button asChild>
                  <Link to="/onboarding">Finish payout setup</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/settings/payouts">View payout ledger</Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {openLinkUrl && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_12px_24px_-22px_rgba(15,23,42,0.2)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Open Payment Link is live</p>
                <p className="mt-1 text-xs text-slate-600">
                  Use this in your bio or print a front-desk sign for walk-in customers.
                </p>
                <code className="mt-2 block max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded bg-slate-50 px-2 py-1 text-xs text-slate-700">
                  {openLinkUrl}
                </code>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={async () => {
                  const copied = await writeClipboardText(openLinkUrl);
                  if (copied) toast.success('Open payment link copied.');
                }}>
                  <Copy className="mr-2 h-4 w-4" /> Copy Open Link
                </Button>
                <Button type="button" variant="outline" onClick={printDeskSign}>
                  Print Desk Sign
                </Button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off" className="mt-8 grid gap-4 md:grid-cols-2">
          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="serviceTemplate">Load a service template</Label>
            <Select value={selectedTemplateId} onValueChange={applyServiceTemplate} disabled={isLoadingTemplates}>
              <SelectTrigger id="serviceTemplate">
                <SelectValue placeholder={isLoadingTemplates ? 'Loading templates...' : 'Select a saved service (optional)'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No template</SelectItem>
                {serviceTemplates.map((template) => (
                  <SelectItem key={template.templateId} value={template.templateId}>
                    {template.name} ({formatCurrency(template.defaultAmount, template.currency)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2 md:col-span-2">
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
              <Checkbox
                id="multiTierEnabled"
                checked={multiTierEnabled}
                onCheckedChange={(checked) => setMultiTierEnabled(checked === true)}
              />
              <div className="grid gap-0.5">
                <Label htmlFor="multiTierEnabled" className="cursor-pointer text-slate-900">
                  Offer multiple options (Good / Better / Best)
                </Label>
                <p className="text-xs text-slate-500">Send one link with up to 3 selectable price options.</p>
              </div>
            </div>
          </div>

          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="totalAmount">Total amount (USD)</Label>
            <Input
              id="totalAmount"
              inputMode="decimal"
              autoComplete="off"
              value={totalAmount}
              onChange={(event) => setTotalAmount(event.target.value)}
              placeholder="Enter total amount"
              required={!multiTierEnabled}
              disabled={multiTierEnabled}
            />
          </div>

          {multiTierEnabled && (
            <div className="grid gap-3 md:col-span-2">
              {multiTierInputs.map((tier, index) => (
                <div key={index} className="rounded-xl border border-slate-200 bg-white p-3 shadow-[0_10px_20px_-18px_rgba(15,23,42,0.18)]">
                  <div className="grid gap-3 md:grid-cols-[1fr_180px]">
                    <div className="grid gap-2">
                      <Label htmlFor={`tier-name-${index}`}>Option {index + 1} Name</Label>
                      <Input
                        id={`tier-name-${index}`}
                        value={tier.name}
                        onChange={(event) =>
                          setMultiTierInputs((prev) => prev.map((row, i) => (i === index ? { ...row, name: event.target.value } : row)))
                        }
                        placeholder={index === 0 ? 'Basic' : index === 1 ? 'Standard' : 'Premium'}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor={`tier-amount-${index}`}>Amount (USD)</Label>
                      <Input
                        id={`tier-amount-${index}`}
                        inputMode="decimal"
                        value={tier.amount}
                        onChange={(event) =>
                          setMultiTierInputs((prev) => prev.map((row, i) => (i === index ? { ...row, amount: event.target.value } : row)))
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2">
                    <Label htmlFor={`tier-desc-${index}`}>Description (optional)</Label>
                    <Input
                      id={`tier-desc-${index}`}
                      value={tier.description}
                      onChange={(event) =>
                        setMultiTierInputs((prev) => prev.map((row, i) => (i === index ? { ...row, description: event.target.value } : row)))
                      }
                      placeholder="What’s included in this option?"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              autoComplete="off"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What is this purchase for?"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="customerName">Customer name (optional)</Label>
            <Input
              id="customerName"
              autoComplete="off"
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
              placeholder="Customer first + last name"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="depositAmount">Deposit amount (optional)</Label>
            <Input
              id="depositAmount"
              inputMode="decimal"
              autoComplete="off"
              value={depositAmount}
              onChange={(event) => setDepositAmount(event.target.value)}
              placeholder="Optional upfront deposit"
              disabled={multiTierEnabled}
            />
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 md:col-span-2">
            <Checkbox
              id="depositOnlyMode"
              checked={depositOnlyCheckoutMode}
              onCheckedChange={(checked) => setDepositOnlyCheckoutMode(checked === true)}
              disabled={multiTierEnabled || Number(depositAmount || '0') <= 0}
            />
            <div className="grid gap-0.5">
              <Label htmlFor="depositOnlyMode" className="cursor-pointer">
                Charge deposit only today
              </Label>
              <p className="text-xs text-slate-500">
                Buyer pays the deposit now. Remaining balance is handled later. For multi-tier quotes, this stays disabled.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 md:col-span-2">
            <Checkbox
              id="tippingEnabled"
              checked={tippingEnabled}
              onCheckedChange={(checked) => setTippingEnabled(checked === true)}
            />
            <div className="grid gap-0.5">
              <Label htmlFor="tippingEnabled" className="cursor-pointer">
                Allow tipping on checkout
              </Label>
              <p className="text-xs text-slate-500">Buyer can add gratuity before secure checkout.</p>
            </div>
          </div>

          <div className="flex items-end">
            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
              <Button type="submit" disabled={isSubmitting} className="w-full rounded-xl bg-slate-900 text-white hover:bg-slate-800 md:w-fit">
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create financing link
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={saveCurrentAsTemplate}
                disabled={isSavingTemplate}
                className="w-full rounded-xl border-slate-200 bg-white hover:bg-slate-50 md:w-fit"
              >
                {isSavingTemplate ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save as template
              </Button>
            </div>
          </div>
        </form>

        {quote && (
          <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/80 p-6">
            <h2 className="text-xl font-semibold tracking-[-0.03em] text-slate-900">Buyer Link Ready</h2>
            <p className="mt-2 text-sm text-slate-600">Financing options appear in checkout when eligible.</p>
            {quote.checkoutMode === 'deposit_only' && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Deposit-only mode: buyer pays {formatCurrency(quote.depositAmount || 0, 'USD')} today. Remaining balance is handled later.
              </div>
            )}
            {quote.tiers && quote.tiers.length > 1 && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-medium text-slate-900">Multi-tier quote options</p>
                <div className="mt-2 grid gap-2">
                  {quote.tiers.map((tier, index) => (
                    <div key={`${tier.name}-${index}`} className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{tier.name}</p>
                        {tier.description ? <p className="text-xs text-slate-500">{tier.description}</p> : null}
                      </div>
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(tier.amount, 'USD')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <code className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded bg-white px-3 py-2 text-xs text-slate-700 sm:max-w-[460px]">
                {quote.shareUrl}
              </code>
              <Button type="button" variant="outline" onClick={copyShareLink}>
                <Copy className="mr-2 h-4 w-4" /> Copy
              </Button>
              {canNativeShare && (
                <Button type="button" variant="outline" onClick={shareNativeLink}>
                  <Share2 className="mr-2 h-4 w-4" /> Share
                </Button>
              )}
              <Button type="button" variant="outline" onClick={sendSmsLink}>
                <MessageSquare className="mr-2 h-4 w-4" /> SMS
              </Button>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-[220px_1fr] md:items-start">
              <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="mx-auto flex flex-col items-center rounded-xl bg-white p-3 text-center shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                    disabled={!qrCodeDataUrl}
                    aria-label="Open larger QR code"
                  >
                    {qrCodeDataUrl ? (
                      <>
                        <img src={qrCodeDataUrl} alt="QR code for financing link" className="h-[196px] w-[196px]" />
                        <p className="mt-2 text-center text-xs font-medium text-slate-600">Click to enlarge for easier scan</p>
                      </>
                    ) : (
                      <div className="grid h-[196px] w-[196px] place-content-center text-center text-xs text-slate-500">
                        QR unavailable in this build
                      </div>
                    )}
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-[420px]">
                  <DialogHeader>
                    <DialogTitle>Larger QR code</DialogTitle>
                    <DialogDescription>Have the buyer scan this to open checkout quickly.</DialogDescription>
                  </DialogHeader>
                  {qrCodeDataUrl ? (
                    <div className="mx-auto rounded-2xl bg-white p-3 shadow-sm">
                      <img src={qrCodeDataUrl} alt="Large QR code for financing link" className="h-[340px] w-[340px]" />
                    </div>
                  ) : null}
                </DialogContent>
              </Dialog>
              <div className="text-sm text-slate-700">
                <p>
                  As low as <strong>{formatCurrency(quote.monthlyEstimate, 'USD')}/mo</strong> or{' '}
                  <strong>{formatCurrency(quote.payIn4Estimate, 'USD')}</strong> in 4 payments.
                </p>
                {quote.checkoutMode === 'deposit_only' && (
                  <p className="mt-2 text-xs text-slate-600">
                    Total project: {formatCurrency(quote.totalAmount || 0, 'USD')} • Deposit charged now: {formatCurrency(quote.depositAmount || 0, 'USD')}
                  </p>
                )}
                <p className="mt-2">Expires: {new Date(quote.expiresAt).toLocaleString()} ({formatCountdown(quote.expiresAt)})</p>
              </div>
            </div>
          </div>
        )}

        {followUpCandidate && (
          <div className="mt-8 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-white p-5 shadow-[0_14px_30px_-24px_rgba(245,158,11,0.45)]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-2xl">
                <p className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-amber-900">
                  <Clock3 className="h-3.5 w-3.5" /> Follow-up moment detected
                </p>
                <h3 className="mt-3 text-lg font-semibold tracking-[-0.02em] text-slate-900">A customer viewed this quote {followUpCandidate.viewedAt ? formatMinutesSince(followUpCandidate.viewedAt) : 'recently'}</h3>
                <p className="mt-1 text-sm text-slate-700">
                  Quote #{followUpCandidate.quoteId.slice(0, 8)} for {formatCurrency(followUpCandidate.financedAmount, followUpCandidate.currency)} is still open. A short, helpful nudge now usually converts better than waiting.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {FOLLOW_UP_TONES.map((tone) => (
                    <button
                      key={tone.id}
                      type="button"
                      className={`rounded-full border px-3 py-1 text-xs transition ${
                        followUpTone === tone.id
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
                      }`}
                      onClick={() => setFollowUpTone(tone.id)}
                      title={tone.helper}
                    >
                      {tone.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => copyFollowUpMessage(followUpCandidate, followUpTone)}>
                  <Copy className="mr-2 h-4 w-4" /> Copy follow-up
                </Button>
                <Button type="button" variant="outline" onClick={() => sendFollowUpSms(followUpCandidate, followUpTone)}>
                  <MessageSquare className="mr-2 h-4 w-4" /> Send SMS
                </Button>
                {notificationPermission === 'default' && (
                  <Button type="button" onClick={enableSmartAlerts}>
                    <Bell className="mr-2 h-4 w-4" /> Enable smart alerts
                  </Button>
                )}
              </div>
            </div>
            {notificationPermission === 'default' && (
              <p className="mt-3 text-xs text-slate-600">
                We only ask because you now have a live buyer signal. Alerts notify you when a customer views a link, so you can follow up while intent is high.
              </p>
            )}
          </div>
        )}

        {staleViewCandidate && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-700">
              Quote #{staleViewCandidate.quoteId.slice(0, 8)} was viewed {staleViewCandidate.viewedAt ? formatMinutesSince(staleViewCandidate.viewedAt) : 'recently'} but has not started checkout.
            </p>
            <div className="mt-3">
              <Button type="button" variant="outline" onClick={() => createRefreshedQuote(staleViewCandidate)} disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create refreshed quote
              </Button>
            </div>
          </div>
        )}

        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_16px_36px_-28px_rgba(15,23,42,0.2)]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold tracking-[-0.03em] text-slate-900">Recent Financing Quotes</h2>
            <Button type="button" variant="ghost" onClick={() => loadRecentQuotes()} disabled={isLoadingQuotes}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoadingQuotes ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          <div className="mt-4 space-y-3">
            {isLoadingQuotes && recentQuotes.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 via-white to-white p-6">
                <div className="flex items-start gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-content-center rounded-xl bg-slate-900 text-white">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold tracking-[-0.02em] text-slate-900">Loading your financing pipeline</h3>
                    <p className="mt-1 text-sm text-slate-600 transition-all duration-500">
                      {STUDIO_LOADING_TIPS[loadingTipIndex]}
                    </p>
                    <div className="mt-3 h-1.5 w-40 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full rounded-full bg-slate-800/80 animate-[progress_4.2s_linear_infinite]"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 grid gap-3">
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100" />
                </div>
                <style>{`
                  @keyframes progress {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                  }
                `}</style>
              </div>
            ) : recentQuotes.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-gradient-to-b from-slate-50 to-white p-8 text-center">
                <div className="mx-auto mb-4 grid h-12 w-12 place-content-center rounded-2xl bg-slate-900 text-white shadow-sm">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold tracking-[-0.03em] text-slate-900">No financing quotes yet</h3>
                <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">
                  Create your first quote above to generate a buyer link, show a QR code in person, and track views, shares, and checkout progress in real time.
                </p>
              </div>
            ) : (
              recentQuotes.map((item) => (
                <div key={item.quoteId} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">{formatCurrency(item.financedAmount, item.currency)}</p>
                      <p className="text-sm text-slate-700">{getQuotePurpose(item)}</p>
                      {item.checkoutMode === 'deposit_only' && (
                        <p className="mt-1 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900">
                          Deposit-only: {formatCurrency(item.depositAmount, item.currency)} charged today
                        </p>
                      )}
                      {item.status === 'deposit_paid' && (
                        <p className="mt-1 text-xs font-medium text-cyan-800">
                          Deposit secured. Remaining balance still pending.
                        </p>
                      )}
                      {item.tiers && item.tiers.length > 1 && (
                        <p className="mt-1 text-xs text-slate-500">
                          Multi-tier: {item.tiers.map((tier) => tier.name).join(' / ')} • Range {formatCurrency(Math.min(...item.tiers.map((tier) => tier.amount)), item.currency)} - {formatCurrency(Math.max(...item.tiers.map((tier) => tier.amount)), item.currency)}
                        </p>
                      )}
                      {item.tiers && item.tiers.length > 1 && item.depositAmount > 0 && (
                        <p className="mt-1 inline-flex rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-900">
                          Shared deposit applied across tiers: {formatCurrency(item.depositAmount, item.currency)}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        Quote #{item.quoteId.slice(0, 8)} • {item.viewCount} view(s) • Shares C:{item.shareEvents.copy} S:{item.shareEvents.sms} Q:{item.shareEvents.qr} N:{item.shareEvents.native}
                      </p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyleMap[item.status]}`}>
                      {statusLabelMap[item.status]}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">Expires: {item.expiresAt ? `${new Date(item.expiresAt).toLocaleString()} (${formatCountdown(item.expiresAt)})` : 'No expiry'}</p>
                  {item.checkoutMode === 'deposit_only' && (
                    <p className="mt-1 text-xs text-slate-500">
                      Project total {formatCurrency(item.totalAmount, item.currency)} • Remaining balance {formatCurrency(Math.max(0, item.totalAmount - item.depositAmount), item.currency)}
                    </p>
                  )}
                  {item.lastSharedAt && item.lastSharedChannel && (
                    <p className="mt-1 text-xs text-slate-500">
                      Last shared via {item.lastSharedChannel.toUpperCase()} at {new Date(item.lastSharedAt).toLocaleString()}
                    </p>
                  )}
                  {item.shareUrl && (
                    <button
                      type="button"
                      className="mt-2 text-xs font-medium text-slate-700 underline"
                      onClick={async () => {
                        const copied = await writeClipboardText(item.shareUrl as string);
                        if (copied) toast.success('Copied link for this quote.');
                      }}
                    >
                      Copy link
                    </button>
                  )}
                  {item.status === 'deposit_paid' && item.totalAmount - item.depositAmount > 0 && (
                    <div className="mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={isSubmitting}
                        onClick={() => createRemainingBalanceQuote(item)}
                      >
                        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                        Create remaining-balance quote
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_16px_36px_-28px_rgba(15,23,42,0.2)]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold tracking-[-0.03em] text-slate-900">Insights Snapshot</h2>
            <span className="text-xs font-medium text-slate-500">Last 30 days</span>
          </div>

          {isLoadingInsights && !insights ? (
            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
              <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
              <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
              <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
            </div>
          ) : insights ? (
            <>
              {insights.isDataTruncated && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Insights are based on the first 5,000 quotes in the selected period. Results may be truncated for high-volume activity.
                </div>
              )}
              <div className="mt-4 grid gap-3 md:grid-cols-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Quotes created</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{insights.summary.quotesCreated}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Buyer view rate</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{insights.rates.viewRatePct}%</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Checkout start rate</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{insights.rates.checkoutStartRatePct}%</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Paid conversion</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{insights.rates.paidRatePct}%</p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                <p className="font-medium text-slate-900">What appears to drive results</p>
                <p className="mt-1">
                  Follow-up actions were used on <strong>{insights.levers.followUpUsedOnPaidPct}%</strong> of paid quotes. Top follow-up channel: <strong>{insights.levers.topShareChannel === 'none' ? 'not enough data yet' : insights.levers.topShareChannel.toUpperCase()}</strong>.
                </p>
                <p className="mt-1">
                  Median speed to first buyer view: <strong>{insights.speed.medianMinutesToFirstView !== null ? `${insights.speed.medianMinutesToFirstView} min` : 'n/a'}</strong>.
                </p>
                {insights.tierPerformance && insights.tierPerformance.multiTierQuotesCreated > 0 && (
                  <>
                    <p className="mt-1">
                      Multi-tier paid rate: <strong>{insights.tierPerformance.multiTierPaidRatePct}%</strong> vs single-price{' '}
                      <strong>{insights.tierPerformance.singleTierPaidRatePct}%</strong>. Top selected tier:{' '}
                      <strong>{insights.tierPerformance.topSelectedTierName || 'n/a'}</strong>.
                    </p>
                    {insights.tierPerformance.selectionBreakdown.length > 0 && (
                      <p className="mt-1 text-xs text-slate-600">
                        Tier selections:{' '}
                        {insights.tierPerformance.selectionBreakdown
                          .map((row) => `${row.tierName} (${row.count})`)
                          .join(' • ')}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-xs text-slate-600">
                {insights.benchmark.networkSalesLiftPct !== null ? (
                  <p>
                    Businesses using LendLifted saw <strong>{insights.benchmark.networkSalesLiftPct}% more sales on average</strong> ({insights.benchmark.confidence} confidence). {insights.benchmark.note}
                  </p>
                ) : (
                  <p>{insights.benchmark.note}</p>
                )}
              </div>
            </>
          ) : (
            <p className="mt-4 text-sm text-slate-500">Insights will populate as quote activity grows.</p>
          )}
        </div>
      </div>
    </section>
  );
}
